// =============================================================
//  PaymentDetailScreen — Détail complet d'un paiement
//
//  Affiche TOUS les champs stockés dans 'purchases' :
//    Communs    : userId, documentId, documentType, paymentMethod,
//                 paymentReference, status, purchaseDate
//    Concours   : titre, annee, filiere, nombrePages, prix, prixInitial,
//                 prixFinal, reductionPourcent, gratuit, groupId
//    Bords      : titre, prix, prixInitial, prixFinal, reductionPourcent,
//                 nombrePages
//
//  Récupère en plus :
//    - L'utilisateur via userId (nom, email, téléphone)
//    - Le document via documentId + documentType (nomComplet, sigle, etc.)
// =============================================================

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Linking } from 'react-native';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Divider, Avatar, Loading } from './web-ui/kit';
import { colors, spacing, radius } from './web-ui/theme';

const FCFA = (n) => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('fr-FR');
};

const statusVariant = (s) => {
  const v = String(s || '').toLowerCase();
  if (['successful', 'success', 'completed', 'paid'].includes(v)) return 'success';
  if (['pending', 'processing'].includes(v)) return 'warning';
  if (['failed', 'cancelled', 'expired'].includes(v)) return 'danger';
  return 'neutral';
};

const statusLabel = (s) => {
  const v = String(s || '').toLowerCase();
  if (['successful', 'success', 'completed', 'paid'].includes(v)) return 'Paiement complété';
  if (['pending', 'processing'].includes(v)) return 'En attente';
  if (['failed', 'cancelled', 'expired'].includes(v)) return 'Paiement échoué';
  return s || '—';
};

const operatorLabel = (m) => {
  const v = String(m || '').toLowerCase();
  if (v.includes('mtn') || v.includes('momo')) return 'MTN Mobile Money';
  if (v.includes('orange')) return 'Orange Money';
  return m || 'Mobile Money';
};

const openUrl = (url) => {
  if (!url) return;
  if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
  else Linking.openURL(url);
};

const docCollection = (type) => {
  if (type === 'concours') return 'concours';
  if (type === 'bords') return 'bords';
  return 'documents';
};

export default function PaymentDetailScreen({ route, navigation }) {
  const payment = route?.params?.payment || {};

  const [userInfo, setUserInfo] = useState(null);
  const [docInfo, setDocInfo] = useState(null);
  const [loadingExtra, setLoadingExtra] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [userSnap, docSnap] = await Promise.all([
          payment.userId
            ? getDoc(doc(db, 'users', payment.userId)).catch(() => null)
            : Promise.resolve(null),
          payment.documentId && payment.documentType
            ? getDoc(doc(db, docCollection(payment.documentType), payment.documentId)).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setUserInfo(userSnap?.exists?.() ? { id: userSnap.id, ...userSnap.data() } : null);
        setDocInfo(docSnap?.exists?.() ? { id: docSnap.id, ...docSnap.data() } : null);
      } catch (e) {
        console.log('payment detail extra load error', e?.message || e);
      } finally {
        if (!cancelled) setLoadingExtra(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payment.userId, payment.documentId, payment.documentType]);

  const amount = Number(payment.amount) || Number(payment.prix) || Number(payment.prixFinal) || 0;
  const itemTitle = payment.titre || payment.documentTitle || docInfo?.sigle || docInfo?.titre || 'Transaction';
  const userName = userInfo?.fullName || userInfo?.centerName || userInfo?.name || '—';
  const userEmail = userInfo?.email || payment.userEmail || '—';
  const userPhone = userInfo?.phone || userInfo?.phoneNumber || payment.phoneNumber || '—';

  // Title selon le type
  const headerLabel =
    payment.documentType === 'concours'
      ? 'Achat de sujet de concours'
      : payment.documentType === 'bords'
      ? 'Achat de bord'
      : 'Achat de document';

  return (
    <AdminLayout title="Détail du paiement" subtitle={headerLabel}>
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
        {!!docInfo && (
          <Button
            icon="eye-arrow-right-outline"
            onPress={() =>
              navigation.navigate('DocumentDetailScreen', {
                document: { ...docInfo, type: payment.documentType },
              })
            }
          >
            Voir le document
          </Button>
        )}
      </View>

      {/* En-tête : ID + statut + montant */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' }}>
          <View style={{ flex: 1, minWidth: 240 }}>
            <Text style={styles.label}>ID</Text>
            <Text style={styles.idText} selectable>{payment.id || '—'}</Text>
            <View style={{ marginTop: 8 }}>
              <Badge variant={statusVariant(payment.status)}>{statusLabel(payment.status)}</Badge>
            </View>
          </View>
          <View style={styles.amountBox}>
            <Text style={styles.amountLabel}>Montant payé</Text>
            <Text style={styles.amount}>{FCFA(amount)}</Text>
          </View>
        </View>
      </Card>

      {/* Informations de paiement */}
      <Card style={styles.spacer}>
        <Text style={styles.sectionTitle}>Informations de paiement</Text>
        <View style={styles.grid}>
          <Info label="Montant" value={FCFA(amount)} />
          <Info label="Date" value={fmtDate(payment.purchaseDate || payment.createdAt)} />
          <Info label="Méthode" value={operatorLabel(payment.paymentMethod || payment.operator)} />
          <Info label="Référence" value={payment.paymentReference || payment.reference || '—'} />
          {!!payment.documentType && <Info label="Type" value={payment.documentType} />}
          {payment.gratuit === true && <Info label="Document" value="Gratuit" />}
        </View>

        {/* Détails spécifiques selon le type */}
        {(payment.titre || payment.annee || payment.filiere || payment.nombrePages || payment.prix != null) && (
          <>
            <Divider />
            <Text style={styles.sectionTitle}>Détail de l'achat</Text>
            <View style={styles.grid}>
              {!!payment.titre && <Info label="Document" value={payment.titre} />}
              {!!payment.filiere && <Info label="Filière" value={payment.filiere} />}
              {!!payment.annee && <Info label="Année" value={payment.annee} />}
              {payment.nombrePages != null && <Info label="Pages" value={payment.nombrePages} />}
              {payment.prix != null && <Info label="Prix" value={FCFA(payment.prix)} />}
              {payment.prixInitial != null && payment.prixInitial !== payment.prix && (
                <Info label="Prix initial" value={FCFA(payment.prixInitial)} />
              )}
              {payment.prixFinal != null && payment.prixFinal !== payment.prix && (
                <Info label="Prix final" value={FCFA(payment.prixFinal)} />
              )}
              {payment.reductionPourcent != null && parseFloat(payment.reductionPourcent) > 0 && (
                <Info label="Réduction" value={`${payment.reductionPourcent}%`} />
              )}
              <Info label="Type" value={payment.gratuit ? 'Gratuit' : 'Payant'} />
              {!!payment.groupId && <Info label="Groupe" value={payment.groupId} />}
            </View>
          </>
        )}
      </Card>

      {/* Utilisateur */}
      <Card style={styles.spacer}>
        <Text style={styles.sectionTitle}>Informations utilisateur</Text>
        {loadingExtra ? (
          <Loading label="Récupération du compte..." />
        ) : userInfo ? (
          <>
            <View style={{ flexDirection: 'row', gap: spacing.md, alignItems: 'center' }}>
              <Avatar
                src={userInfo.photoURL || userInfo.imageUrl}
                name={userName}
                size={56}
              />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.userName}>{userName}</Text>
                <Text style={styles.userMeta}>{userEmail}</Text>
                <Text style={styles.userMeta}>{userPhone}</Text>
              </View>
              <Button
                size="sm"
                variant="secondary"
                icon="account-arrow-right-outline"
                onPress={() =>
                  navigation.navigate('UserDetailScreen', {
                    user: userInfo,
                    userType: userInfo.userType || 'users',
                  })
                }
              >
                Voir profil
              </Button>
            </View>
            <Divider />
            <View style={styles.grid}>
              <Info label="ID utilisateur" value={payment.userId} />
              {!!userInfo.userType && <Info label="Type" value={userInfo.userType} />}
              {!!userInfo.region && <Info label="Région" value={userInfo.region} />}
              {!!userInfo.gender && <Info label="Genre" value={userInfo.gender} />}
              {userInfo.age != null && <Info label="Âge" value={userInfo.age} />}
              {!!userInfo.status && <Info label="Statut compte" value={userInfo.status} />}
            </View>
          </>
        ) : (
          <Text style={styles.subMeta}>
            Utilisateur introuvable {payment.userId ? `(ID: ${payment.userId})` : ''}
          </Text>
        )}
      </Card>

      {/* Détails du document lié */}
      <Card style={styles.spacer}>
        <Text style={styles.sectionTitle}>Détails du document</Text>
        {loadingExtra ? (
          <Loading label="Récupération du document..." />
        ) : docInfo ? (
          <>
            <Text style={styles.docTitle}>
              {docInfo.sigle || docInfo.titre || docInfo.nomComplet || itemTitle}
            </Text>
            {!!docInfo.nomComplet && docInfo.sigle !== docInfo.nomComplet && (
              <Text style={styles.userMeta}>{docInfo.nomComplet}</Text>
            )}
            {!!docInfo.description && (
              <Text style={[styles.body, { marginTop: spacing.sm }]}>{docInfo.description}</Text>
            )}
            <View style={[styles.grid, { marginTop: spacing.md }]}>
              <Info label="Type" value={payment.documentType} />
              {!!docInfo.langue && <Info label="Langue" value={docInfo.langue} />}
              {!!docInfo.niveau && <Info label="Niveau" value={docInfo.niveau} />}
              {!!docInfo.typeConcours && <Info label="Type concours" value={docInfo.typeConcours} />}
              {!!docInfo.status && <Info label="Statut" value={docInfo.status} />}
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: spacing.md }}>
              <Button
                icon="eye-arrow-right-outline"
                onPress={() =>
                  navigation.navigate('DocumentDetailScreen', {
                    document: { ...docInfo, type: payment.documentType },
                  })
                }
              >
                Voir le document
              </Button>
            </View>
          </>
        ) : (
          <Text style={styles.subMeta}>
            Document introuvable {payment.documentId ? `(ID: ${payment.documentId})` : ''}
          </Text>
        )}
      </Card>
    </AdminLayout>
  );
}

function Info({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={2}>{value ?? '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  spacer: { marginTop: spacing.lg },
  label: { fontSize: 11, color: colors.textMuted, fontWeight: '700', textTransform: 'uppercase' },
  idText: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: colors.text,
    marginTop: 4,
  },
  amountBox: {
    alignItems: 'flex-end',
    minWidth: 180,
  },
  amountLabel: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  amount: {
    fontSize: 30,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -0.5,
    marginTop: 4,
  },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.md },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
  userName: { fontSize: 16, fontWeight: '700', color: colors.text },
  userMeta: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },
  body: { fontSize: 14, color: colors.textSecondary, lineHeight: 21 },
  subMeta: { fontSize: 13, color: colors.textSecondary, padding: spacing.md },
  docTitle: { fontSize: 18, fontWeight: '700', color: colors.text },
});
